import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  requestGetTranslationOverviewCatalogLocales,
  requestGetTranslationOverviewCatalogs,
  requestGetTranslationOverviewLocaleCatalogs,
  requestGetTranslationOverviewLocales,
  requestRefreshTranslationOverview,
  requestTranslateTranslationOverview,
  requestUploadPendingTranslationOverview,
  requestUploadTranslationOverviewLocale,
} from '~/queries/customerCommunication/requestTranslationOverview';

import type {
  TranslationOverviewCatalogListParams,
  TranslationOverviewCatalogLocaleRow,
  TranslationOverviewCatalogRow,
  TranslationOverviewDetailParams,
  TranslationOverviewListParams,
  TranslationOverviewListResponse,
  TranslationOverviewLocaleCatalogRow,
  TranslationOverviewLocaleRow,
  TranslationOverviewQueuedResponse,
  TranslationOverviewTranslatePayload,
  TranslationOverviewUploadPayload,
  TranslationOverviewUploadPendingPayload,
  TranslationOverviewUploadQueuedResponse,
  TranslationOverviewUploadResult,
} from '~/components/CustomerCommunication/TranslationOverview/types';

export const translationOverviewQueryKeys = {
  all: ['translationOverview'] as const,
  catalogLists: () => [...translationOverviewQueryKeys.all, 'catalogList'] as const,
  catalogList: (params: TranslationOverviewCatalogListParams) =>
    [...translationOverviewQueryKeys.catalogLists(), params] as const,
  localeLists: () => [...translationOverviewQueryKeys.all, 'localeList'] as const,
  localeList: (params: TranslationOverviewListParams) => [...translationOverviewQueryKeys.localeLists(), params],
  catalogDetails: () => [...translationOverviewQueryKeys.all, 'catalogDetail'] as const,
  catalogDetail: (catalogId: string, params: TranslationOverviewDetailParams) =>
    [...translationOverviewQueryKeys.catalogDetails(), catalogId, params] as const,
  localeDetails: () => [...translationOverviewQueryKeys.all, 'localeDetail'] as const,
  localeDetail: (locale: string, params: TranslationOverviewDetailParams) =>
    [...translationOverviewQueryKeys.localeDetails(), locale, params] as const,
};

export function useGetTranslationOverviewCatalogs(params: TranslationOverviewCatalogListParams) {
  return useQuery<TranslationOverviewListResponse<TranslationOverviewCatalogRow>>({
    queryKey: translationOverviewQueryKeys.catalogList(params),
    queryFn: ({ signal }) => requestGetTranslationOverviewCatalogs(params, signal),
    // Keeps the previous page rendered while the next one loads, so the grid does not collapse on every page change.
    placeholderData: keepPreviousData,
  });
}

export function useGetTranslationOverviewLocales(params: TranslationOverviewListParams) {
  return useQuery<TranslationOverviewListResponse<TranslationOverviewLocaleRow>>({
    queryKey: translationOverviewQueryKeys.localeList(params),
    queryFn: ({ signal }) => requestGetTranslationOverviewLocales(params, signal),
    placeholderData: keepPreviousData,
  });
}

export function useGetTranslationOverviewCatalogLocales(
  catalogId: string | undefined,
  params: TranslationOverviewDetailParams,
) {
  return useQuery<TranslationOverviewListResponse<TranslationOverviewCatalogLocaleRow>>({
    queryKey: translationOverviewQueryKeys.catalogDetail(catalogId ?? '', params),
    queryFn: ({ signal }) => requestGetTranslationOverviewCatalogLocales(catalogId, params, signal),
    enabled: !!catalogId,
    placeholderData: keepPreviousData,
  });
}

export function useGetTranslationOverviewLocaleCatalogs(
  locale: string | undefined,
  params: TranslationOverviewDetailParams,
) {
  return useQuery<TranslationOverviewListResponse<TranslationOverviewLocaleCatalogRow>>({
    queryKey: translationOverviewQueryKeys.localeDetail(locale ?? '', params),
    queryFn: ({ signal }) => requestGetTranslationOverviewLocaleCatalogs(locale, params, signal),
    enabled: !!locale,
    placeholderData: keepPreviousData,
  });
}

export function useTranslateTranslationOverview() {
  const queryClient = useQueryClient();

  return useMutation<TranslationOverviewQueuedResponse, Error, TranslationOverviewTranslatePayload>({
    mutationFn: requestTranslateTranslationOverview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: translationOverviewQueryKeys.all });
    },
  });
}

export function useRefreshTranslationOverview() {
  const queryClient = useQueryClient();

  return useMutation<TranslationOverviewQueuedResponse, Error, TranslationOverviewTranslatePayload>({
    mutationFn: requestRefreshTranslationOverview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: translationOverviewQueryKeys.all });
    },
  });
}

export function useUploadPendingTranslationOverview() {
  const queryClient = useQueryClient();

  return useMutation<TranslationOverviewUploadQueuedResponse, Error, TranslationOverviewUploadPendingPayload>({
    mutationFn: requestUploadPendingTranslationOverview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: translationOverviewQueryKeys.all });
    },
  });
}

export function useUploadTranslationOverviewLocale() {
  const queryClient = useQueryClient();

  return useMutation<Array<TranslationOverviewUploadResult>, Error, TranslationOverviewUploadPayload>({
    mutationFn: requestUploadTranslationOverviewLocale,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: translationOverviewQueryKeys.all });
    },
  });
}
